import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Tag, BarChart3, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const CreateBrief = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedBriefId, setSavedBriefId] = useState(null);

  // Check for pre-filled keywords from Keyword Research dashboard
  const prefillKws = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem('prefill_keywords');
      const data = sessionStorage.getItem('prefill_keyword_data');
      sessionStorage.removeItem('prefill_keywords');
      sessionStorage.removeItem('prefill_keyword_data');
      return { keywords: raw ? JSON.parse(raw) : [], data: data ? JSON.parse(data) : [] };
    } catch (e) { return { keywords: [], data: [] }; }
  }, []);

  const [briefData, setBriefData] = useState({
    project_id: '',
    objective: 'Leads',
    budget_total: '',
    budget_daily: '',
    start_date: '',
    end_date: '',
    platforms: ['Google_Ads'],
    ad_products: ['Search'],
    industry_vertical: 'B2B_SaaS',
    target_audience: {},
    mandatories: { required_keywords: prefillKws.keywords },
    landing_pages: [],
    kpi_targets: {},
    naming_convention: 'Client/Channel/CampaignType/Objective/Audience/Goal',
    tracking_nomenclature: 'Skai+GA4',
    additional_notes: ''
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [landingPageInput, setLandingPageInput] = useState('');

  // Track which fields were auto-calculated (not manually typed)
  const [autoField, setAutoField] = useState(null); // 'end_date' | 'budget_daily' | null

  // Smart budget/date auto-calculation
  const recalc = (updated, changedField) => {
    const total = parseFloat(updated.budget_total);
    const daily = parseFloat(updated.budget_daily);
    const start = updated.start_date;
    const end = updated.end_date;

    // User manually typed into a previously auto-filled field — clear auto tracking
    if (changedField === autoField) {
      setAutoField(null);
      return updated;
    }

    if (total > 0 && daily > 0 && start && (!end || autoField === 'end_date')) {
      const days = Math.ceil(total / daily);
      const d = new Date(start);
      d.setDate(d.getDate() + days);
      updated.end_date = d.toISOString().split('T')[0];
      setAutoField('end_date');
    } else if (total > 0 && start && end && (!daily || autoField === 'budget_daily')) {
      const ms = new Date(end) - new Date(start);
      const days = Math.max(1, Math.round(ms / 86400000));
      updated.budget_daily = Math.round((total / days) * 100) / 100;
      setAutoField('budget_daily');
    }

    return updated;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      
      // Clean up empty strings to null so backend doesn't choke on them
      const cleanData = { ...briefData };
      if (!cleanData.budget_daily && cleanData.budget_daily !== 0) cleanData.budget_daily = null;
      if (!cleanData.budget_total && cleanData.budget_total !== 0) cleanData.budget_total = null;
      if (!cleanData.end_date) cleanData.end_date = null;
      if (!cleanData.project_id) cleanData.project_id = null;
      if (!cleanData.naming_convention) cleanData.naming_convention = null;
      if (!cleanData.tracking_nomenclature) cleanData.tracking_nomenclature = 'Skai+GA4';
      if (!cleanData.additional_notes) cleanData.additional_notes = null;

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/briefs/paid-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanData)
      });

      if (response.ok) {
        const data = await response.json();
        setSavedBriefId(data.brief.id);
        toast.success('Brief saved successfully! You can now generate campaigns.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create brief');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCampaigns = async (briefId) => {
    setGenerating(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/briefs/${briefId}/generate-campaigns`,
        { method: 'POST' }
      );

      if (!response.ok) {
        toast.error('Failed to start campaign generation');
        setGenerating(false);
        return;
      }

      toast.success('Campaign generation started — redirecting to brief detail...');
      navigate(`/internal/workflows/briefs/${briefId}`);
    } catch (error) {
      toast.error('Network error during generation');
      setGenerating(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setBriefData({
        ...briefData,
        mandatories: {
          ...briefData.mandatories,
          required_keywords: [...briefData.mandatories.required_keywords, keywordInput.trim()]
        }
      });
      setKeywordInput('');
    }
  };

  const addLandingPage = () => {
    if (landingPageInput.trim()) {
      setBriefData({
        ...briefData,
        landing_pages: [...briefData.landing_pages, landingPageInput.trim()]
      });
      setLandingPageInput('');
    }
  };

  return (
    <div className="min-h-screen bg-surface-page p-6 md:p-8 lg:p-12">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/internal/workflows')}
          className="flex items-center gap-2 text-brand hover:text-text-primary mb-6"
        >
          <ArrowLeft size={20} />
          Back to Workflows
        </button>

        <h1 className="text-3xl font-semibold tracking-tight text-text-primary mb-2">Create Paid Search Brief</h1>
        <p className="text-text-secondary mb-8">AI will generate optimal campaign structure for Skai</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Objective */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-brand">Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Objective</Label>
                <select
                  value={briefData.objective}
                  onChange={(e) => setBriefData({ ...briefData, objective: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white"
                >
                  <option value="Leads">Leads</option>
                  <option value="Sales">Sales</option>
                  <option value="Traffic">Traffic</option>
                  <option value="Brand_Awareness">Brand Awareness</option>
                  <option value="App_Installs">App Installs</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Budget ($)</Label>
                  <Input
                    data-testid="budget-total-input"
                    type="number"
                    value={briefData.budget_total}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBriefData(prev => recalc({ ...prev, budget_total: v ? parseFloat(v) : '' }, 'budget_total'));
                    }}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Daily Budget ($)</Label>
                  <Input
                    data-testid="budget-daily-input"
                    type="number"
                    value={briefData.budget_daily}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBriefData(prev => recalc({ ...prev, budget_daily: v ? parseFloat(v) : '' }, 'budget_daily'));
                    }}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    data-testid="start-date-input"
                    type="date"
                    value={briefData.start_date}
                    onChange={(e) => setBriefData(prev => recalc({ ...prev, start_date: e.target.value }, 'start_date'))}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    data-testid="end-date-input"
                    type="date"
                    value={briefData.end_date}
                    onChange={(e) => setBriefData(prev => recalc({ ...prev, end_date: e.target.value }, 'end_date'))}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label>Industry Vertical</Label>
                <select
                  value={briefData.industry_vertical}
                  onChange={(e) => setBriefData({ ...briefData, industry_vertical: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-input rounded-xl bg-white"
                >
                  <option value="B2B_SaaS">B2B SaaS</option>
                  <option value="Ecommerce">E-commerce</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Travel">Travel</option>
                  <option value="Education">Education</option>
                  <option value="Real_Estate">Real Estate</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Keywords */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-brand">Required Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              {prefillKws.data.length > 0 && (
                <div className="mb-4 px-3 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg" data-testid="prefill-badge">
                  <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full uppercase mr-2">SEMRush</span>
                  <span className="text-sm text-green-800">{prefillKws.data.length} keywords imported from Keyword Research</span>
                </div>
              )}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Enter keyword..."
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" onClick={addKeyword}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {briefData.mandatories.required_keywords.map((kw, idx) => {
                  const kwData = prefillKws.data.find(d => d.keyword === kw);
                  return (
                    <span key={`item-${idx}`} className="inline-flex items-center gap-1 px-3 py-1 bg-brand/10 text-brand rounded-full text-sm">
                      {kw}
                      {kwData && <span className="text-xs text-text-tertiary ml-1">{parseInt(kwData.search_volume).toLocaleString()} vol · ${parseFloat(kwData.cpc).toFixed(2)}</span>}
                      <button type="button" className="ml-1 text-brand/50 hover:text-negative" onClick={() => setBriefData(prev => ({
                        ...prev, mandatories: { ...prev.mandatories, required_keywords: prev.mandatories.required_keywords.filter((_, i) => i !== idx) }
                      }))}>×</button>
                    </span>
                  );
                })}
              </div>
            </CardContent>
          </Card>


          {/* Naming Convention & Tracking */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-brand flex items-center gap-2">
                <Tag size={18} />
                Naming Convention & Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="flex items-center gap-1.5 mb-2">
                  BCM Naming Convention
                  <span className="inline-flex items-center" title="Applied to all campaign, ad group, and ad names in the generated output">
                    <Info size={14} className="text-text-tertiary" />
                  </span>
                </Label>
                <select
                  data-testid="naming-convention-select"
                  value={briefData.naming_convention}
                  onChange={(e) => setBriefData({ ...briefData, naming_convention: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-xl bg-white text-sm"
                >
                  <option value="Client/Channel/CampaignType/Objective/Audience/Goal">
                    Client / Channel / Campaign Type / Objective / Audience / Goal
                  </option>
                  <option value="Client/Platform/CampaignType/MatchType/Geo/Date">
                    Client / Platform / Campaign Type / Match Type / Geo / Date
                  </option>
                  <option value="Brand/Channel/Vertical/FunnelStage/Audience/Objective">
                    Brand / Channel / Vertical / Funnel Stage / Audience / Objective
                  </option>
                  <option value="custom">Custom</option>
                </select>
                {briefData.naming_convention === 'custom' && (
                  <Input
                    data-testid="naming-convention-custom-input"
                    placeholder="e.g. Client/Channel/Type/Objective/Audience"
                    className="mt-2"
                    onChange={(e) => setBriefData({ ...briefData, naming_convention: e.target.value || 'custom' })}
                  />
                )}
                {briefData.naming_convention && briefData.naming_convention !== 'custom' && (
                  <div className="mt-3 px-3 py-2.5 bg-nordic/5 border border-nordic/10 rounded-lg" data-testid="naming-convention-preview">
                    <p className="text-xs font-semibold uppercase tracking-wider text-nordic/70 mb-1.5">Preview</p>
                    <p className="text-sm font-mono text-black-stallion/80">
                      {briefData.naming_convention.split('/').map((seg, i) => {
                        const examples = {
                          'Client': 'BCM',
                          'Channel': 'Google_Ads',
                          'CampaignType': 'Brand',
                          'Objective': briefData.objective || 'Leads',
                          'Audience': 'Decision_Makers',
                          'Goal': 'Revenue',
                          'Platform': 'Google_Ads',
                          'MatchType': 'Exact',
                          'Geo': 'US_Northeast',
                          'Date': '2026Q1',
                          'Brand': 'BCM',
                          'Vertical': briefData.industry_vertical?.replace('_', ' ') || 'B2B SaaS',
                          'FunnelStage': 'Bottom',
                        };
                        const val = examples[seg.trim()] || seg.trim();
                        return (
                          <span key={`seg-${i}`}>
                            {i > 0 && <span className="text-nordic/40 mx-0.5">/</span>}
                            <span className="text-nordic">{val}</span>
                          </span>
                        );
                      })}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="flex items-center gap-1.5 mb-2">
                  <BarChart3 size={14} />
                  Tracking Platform
                </Label>
                <select
                  data-testid="tracking-nomenclature-select"
                  value={briefData.tracking_nomenclature}
                  onChange={(e) => setBriefData({ ...briefData, tracking_nomenclature: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-xl bg-white text-sm"
                >
                  <option value="Skai+GA4">Skai + GA4 (Recommended)</option>
                  <option value="Skai+Adobe">Skai + Adobe Analytics</option>
                  <option value="SA360+GA4">SA360 + GA4</option>
                  <option value="Manual">Manual / Custom Tracking</option>
                </select>
                <p className="mt-1.5 text-xs text-text-tertiary">
                  All generated campaigns will include UTM parameters and conversion event mappings for the selected platform.
                </p>
              </div>

              {/* Policy badge */}
              <div className="flex items-center gap-2 px-3 py-2 bg-positive/8 border border-positive/20 rounded-lg" data-testid="policy-badge">
                <span className="w-2 h-2 rounded-full bg-positive flex-shrink-0" />
                <span className="text-xs text-positive font-medium">
                  Policy: Search Only &middot; EFFICIENCY Mode &middot; No GDN/PMax &middot; ROI-First
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Landing Pages */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-brand">Landing Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="https://example.com/landing"
                  value={landingPageInput}
                  onChange={(e) => setLandingPageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLandingPage())}
                />
                <Button type="button" onClick={addLandingPage}>Add</Button>
              </div>
              <div className="space-y-2">
                {briefData.landing_pages.map((url, idx) => (
                  <div key={`item-${idx}`} className="px-3 py-2 bg-white/50 rounded-lg text-sm">
                    {url}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {!savedBriefId ? (
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover text-white h-12 text-lg"
              data-testid="create-brief-submit"
            >
              {loading ? 'Saving Brief...' : (
                <>
                  <Sparkles className="mr-2" size={20} />
                  Save Brief
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-positive/10 border border-positive/30 text-positive text-center font-semibold">
                Brief saved successfully
              </div>
              <Button
                type="button"
                disabled={generating}
                onClick={() => handleGenerateCampaigns(savedBriefId)}
                className="w-full bg-brand hover:bg-brand-hover text-white h-12 text-lg"
                data-testid="generate-campaigns-btn"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Starting generation...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={20} />
                    Generate AI Campaign Structure
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateBrief;
